import React, { memo, useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Image, ImageStyle, ImageContentFit } from 'expo-image';
import { imageCacheService } from '@/services/imageCacheService';
import { ensureWebpFormat, ensureWebpFormatWithWidth } from '@/utils/imageResizer';

// Absolute ceiling for web card requests; covers the largest card at 2x DPI.
const WEB_IMAGE_MAX_WIDTH = 640;

const blurhash = 'L6PZfSi_.AyE_3t7t7R**0o#DgR4';

interface CachedSerieImageProps {
  thumbnailUrl: string;
  seriesId: string;
  style: ImageStyle;
  autoDownload?: boolean;
  resizeMode?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  priority?: 'low' | 'normal' | 'high';
  accessibilityLabel?: string;
  /** Sets the HTML `loading` attribute on web (`expo-image` forwards it). */
  loading?: 'eager' | 'lazy';
  onError?: () => void;
}

const resizeModeToContentFit: Record<string, ImageContentFit> = {
  cover: 'cover',
  contain: 'contain',
  fill: 'fill',
  none: 'none',
  'scale-down': 'scale-down',
};

const CachedSerieImageComponent: React.FC<CachedSerieImageProps> = ({
  thumbnailUrl: rawThumbnailUrl,
  seriesId,
  style,
  autoDownload = true,
  resizeMode = 'cover',
  priority,
  accessibilityLabel,
  loading,
  onError,
}) => {
  // On web, request a right-sized cover based on the rendered card width so we
  // don't download full-resolution images into small slots. Native keeps its
  // existing behavior (and its own disk cache keyed on the un-resized URL).
  const thumbnailUrl = useMemo(() => {
    if (Platform.OS !== 'web') {
      return ensureWebpFormat(rawThumbnailUrl);
    }
    const flat = StyleSheet.flatten(style) as { width?: number | string } | undefined;
    const styleWidth = typeof flat?.width === 'number' ? flat.width : undefined;
    if (!styleWidth) {
      return ensureWebpFormat(rawThumbnailUrl);
    }
    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? Math.min(window.devicePixelRatio, 2)
        : 2;
    const targetWidth = Math.min(Math.round(styleWidth * dpr), WEB_IMAGE_MAX_WIDTH);
    return ensureWebpFormatWithWidth(rawThumbnailUrl, targetWidth);
  }, [rawThumbnailUrl, style]);

  const [imageUri, setImageUri] = useState<string>(thumbnailUrl);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!thumbnailUrl || Platform.OS === 'web') {
      setImageUri(thumbnailUrl);
      return;
    }

    let isMounted = true;

    const loadCachedImage = async () => {
      try {
        const result = await imageCacheService.getCachedImageUri(
          thumbnailUrl,
          seriesId,
          autoDownload
        );
        if (isMounted && result.uri) {
          setImageUri(result.uri);
        }
      } catch (error) {
        if (isMounted) {
          setImageUri(thumbnailUrl);
        }
      }
    };

    loadCachedImage();

    return () => {
      isMounted = false;
    };
  }, [thumbnailUrl, seriesId, autoDownload]);

  if (!thumbnailUrl || hasError) {
    return <View style={[style, styles.placeholder]} />;
  }

  return (
    <Image
      source={{ uri: imageUri }}
      style={style}
      contentFit={resizeModeToContentFit[resizeMode] || 'cover'}
      placeholder={{ blurhash }}
      transition={priority === 'high' ? 0 : 200}
      cachePolicy="memory-disk"
      recyclingKey={`cached-${seriesId}`}
      priority={priority}
      // Always emit an `alt` on web: use the caller's label when provided,
      // otherwise an empty string (decorative) so the attribute is never missing
      // (PageSpeed / a11y "image elements have [alt] attributes").
      accessibilityLabel={accessibilityLabel ?? ''}
      loading={loading}
      onError={() => {
        setHasError(true);
        onError?.();
      }}
    />
  );
};

const styles = StyleSheet.create({
  placeholder: {
    backgroundColor: '#2a2a2a',
  },
});

export const CachedSerieImage = memo(CachedSerieImageComponent, (prevProps, nextProps) => {
  return (
    prevProps.thumbnailUrl === nextProps.thumbnailUrl &&
    prevProps.seriesId === nextProps.seriesId &&
    prevProps.resizeMode === nextProps.resizeMode &&
    prevProps.autoDownload === nextProps.autoDownload &&
    prevProps.priority === nextProps.priority &&
    prevProps.accessibilityLabel === nextProps.accessibilityLabel &&
    prevProps.loading === nextProps.loading
  );
});
